import { useState, useEffect, useRef } from "react";
import { Title } from "@/components/common/typography/Title";
import { pricingData, additionalFeesData } from "@/data/pricing";
import { Button } from "@nextui-org/react";
import { PrimaryButton } from "@/components/common/buttons/PrimaryButton";

type Person = {
  id: string;
  selectedCourse: string;
  selectedCategory: string;
  selectedSize: string;
  includePassport: boolean;
  includeDobok: boolean;
};

const formatPrice = (price: number) => {
  return price % 1 === 0 ? `${Math.floor(price)}` : price.toFixed(2);
};

export const PricingCalculator = () => {
  const lastPersonRef = useRef<HTMLDivElement>(null);
  const [isAddingDisabled, setIsAddingDisabled] = useState(false);
  const [people, setPeople] = useState<Person[]>([
    {
      id: "1",
      selectedCourse: "",
      selectedCategory: "",
      selectedSize: "",
      includePassport: false,
      includeDobok: false,
    },
  ]);

  // Force additional fees when required for each person
  useEffect(() => {
    setPeople((currentPeople) =>
      currentPeople.map((person) => {
        const selectedCourseData = pricingData.find(
          (course) => course.id === person.selectedCourse,
        );
        return {
          ...person,
          includePassport: selectedCourseData?.additionalFees || false,
          includeDobok: person.includeDobok,
        };
      }),
    );
  }, [people.map((p) => p.selectedCourse).join(",")]);

  const calculatePersonTotal = (person: Person, index: number) => {
    let total = 0;
    const selectedCourseData = pricingData.find(
      (course) => course.id === person.selectedCourse,
    );

    if (selectedCourseData && person.selectedCategory) {
      const categoryPrice =
        selectedCourseData.categories.find(
          (cat) => cat.category === person.selectedCategory,
        )?.price || 0;
      total += categoryPrice;
    }

    if (person.includePassport) {
      const passportFee =
        additionalFeesData.find((fee) => fee.type === "PASSPORT")?.price || 0;
      total += passportFee;
    }

    if (person.includeDobok && person.selectedSize) {
      const dobokFee =
        additionalFeesData
          .find((fee) => fee.type === "DOBOK")
          ?.sizes?.find((size) => size.size === person.selectedSize)?.price ||
        0;
      total += dobokFee;
    }

    // Apply 10% discount for additional people
    if (index > 0) {
      total = total * 0.9;
    }

    return total;
  };

  const calculateGrandTotal = () => {
    return people.reduce(
      (total, person, index) => total + calculatePersonTotal(person, index),
      0,
    );
  };

  const isFormComplete = () => {
    return people.every((person) => {
      const courseData = pricingData.find(
        (course) => course.id === person.selectedCourse,
      );

      // Vérification des champs de base
      const hasBasicFields = person.selectedCourse && person.selectedCategory;

      // Si le cours nécessite des frais supplémentaires
      if (courseData?.additionalFees) {
        // Le passeport est obligatoire et le dobok doit avoir une taille si sélectionné
        return (
          hasBasicFields &&
          person.includePassport &&
          (!person.includeDobok || (person.includeDobok && person.selectedSize))
        );
      }

      return hasBasicFields;
    });
  };

  const addPerson = () => {
    if (isAddingDisabled) return;

    setIsAddingDisabled(true);
    setPeople((currentPeople) => [
      ...currentPeople,
      {
        id: (currentPeople.length + 1).toString(),
        selectedCourse: "",
        selectedCategory: "",
        selectedSize: "",
        includePassport: false,
        includeDobok: false,
      },
    ]);

    // Scroll immédiatement
    setTimeout(() => {
      lastPersonRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 0);

    // Reset du bouton après 3 secondes
    setTimeout(() => {
      setIsAddingDisabled(false);
    }, 3000);
  };

  const removePerson = (id: string) => {
    setPeople((currentPeople) =>
      currentPeople.filter((person) => person.id !== id),
    );
  };

  const updatePerson = (id: string, updates: Partial<Person>) => {
    setPeople((currentPeople) =>
      currentPeople.map((person) =>
        person.id === id ? { ...person, ...updates } : person,
      ),
    );
  };

  return (
    <section className="-mt-[1px] flex flex-col gap-16 bg-secondary px-4 py-20 ph:px-24 md:px-48 lg:px-72 xl:px-96">
      <div className="flex flex-col gap-6">
        <Title
          text="Calculateur de prix"
          position="left"
          color="primary"
          className="hidden md:flex"
        />
        <Title
          text={["Calculateur", "de prix"]}
          position="left"
          color="primary"
          textPosition="left"
          className="hidden xs:flex md:hidden"
        />
        <Title
          text={["Calcula", "-teur", "de prix"]}
          position="left"
          textPosition="left"
          color="primary"
          className="flex xs:hidden"
        />

        <div className="flex flex-col gap-2">
          <p className="text-md text-gray-600">
            Vous pouvez ajouter une personne supplémentaire pour bénéficier
            d&apos;une réduction de 10% sur le total.
          </p>

          <PrimaryButton
            text="AJOUTER UNE PERSONNE"
            variant="primary"
            onClick={addPerson}
            disabled={isAddingDisabled}
          />
        </div>
      </div>

      <div className="flex flex-col gap-8">
        {people.map((person, index) => (
          <div
            key={person.id}
            ref={index === people.length - 1 ? lastPersonRef : null}
            className="flex flex-col gap-8 rounded-lg bg-white p-8 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-medium text-primary">
                {index === 0 ? "Première personne" : `Personne ${index + 1}`}
                {index > 0 && " (-10%)"}
              </h2>
              {index > 0 && (
                <Button
                  color="danger"
                  variant="light"
                  onPress={() => removePerson(person.id)}
                  radius="sm"
                >
                  Supprimer
                </Button>
              )}
            </div>

            {/* Course Selection */}
            <div className="flex flex-col gap-4">
              <h3 className="text-sm font-light uppercase tracking-wider text-gray-600">
                Choisissez votre cours
              </h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                {pricingData.map((course) => (
                  <label
                    key={course.id}
                    className="group relative flex cursor-pointer items-center"
                  >
                    <input
                      type="checkbox"
                      className="peer hidden"
                      checked={person.selectedCourse === course.id}
                      onChange={() => {
                        if (person.selectedCourse === course.id) {
                          updatePerson(person.id, {
                            selectedCourse: "",
                            selectedCategory: "",
                            selectedSize: "",
                          });
                        } else {
                          updatePerson(person.id, {
                            selectedCourse: course.id,
                            selectedCategory: "",
                            selectedSize: "",
                          });
                        }
                      }}
                    />
                    <div className="flex h-14 w-full items-center rounded-lg border bg-white px-4 shadow-sm transition-all duration-200 group-hover:shadow-md peer-checked:border-secondary peer-checked:bg-secondary peer-checked:shadow-md">
                      <span className="text-md font-light text-primary ph:text-lg">
                        {course.title}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Category Selection */}
            {person.selectedCourse && (
              <div className="flex flex-col gap-4">
                <h3 className="text-sm font-light uppercase tracking-wider text-gray-600">
                  Catégorie d&apos;âge
                </h3>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {pricingData
                    .find((course) => course.id === person.selectedCourse)
                    ?.categories.map((cat) => (
                      <label
                        key={cat.category}
                        className="group relative flex cursor-pointer items-center"
                      >
                        <input
                          type="checkbox"
                          className="peer hidden"
                          checked={person.selectedCategory === cat.category}
                          onChange={() => {
                            if (person.selectedCategory === cat.category) {
                              updatePerson(person.id, { selectedCategory: "" });
                            } else {
                              updatePerson(person.id, {
                                selectedCategory: cat.category,
                              });
                            }
                          }}
                        />
                        <div className="flex h-14 w-full items-center justify-between rounded-lg border bg-white px-4 shadow-sm transition-all duration-200 group-hover:shadow-md peer-checked:border-secondary peer-checked:bg-secondary peer-checked:shadow-md">
                          <span className="text-md font-light text-primary ph:text-lg">
                            {cat.category.charAt(0) +
                              cat.category.slice(1).toLowerCase()}
                          </span>
                          <span className="font-medium text-primary">
                            {cat.price}€
                          </span>
                        </div>
                      </label>
                    ))}
                </div>
              </div>
            )}

            {/* Additional Fees */}
            {person.selectedCategory &&
              pricingData.find((course) => course.id === person.selectedCourse)
                ?.additionalFees && (
                <div className="flex flex-col gap-4">
                  <h3 className="text-sm font-light uppercase tracking-wider text-gray-600">
                    Frais supplémentaires
                  </h3>
                  <div className="flex flex-col gap-4">
                    {/* Passport */}
                    <div className="flex items-center justify-between rounded-lg bg-white p-4 shadow-sm">
                      <div className="flex flex-col gap-1">
                        <span className="text-lg font-light text-primary">
                          Passeport sportif
                        </span>
                        <span className="text-sm text-gray-500">
                          Obligatoire pour la pratique du Taekwondo
                        </span>
                      </div>
                      <span className="text-lg font-medium text-primary">
                        {additionalFeesData.find(
                          (fee) => fee.type === "PASSPORT",
                        )?.price || 0}
                        €
                      </span>
                    </div>

                    {/* Dobok */}
                    <div className="flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-1">
                          <span className="text-lg font-light text-primary">
                            Dobok (tenue officielle)
                          </span>
                          <span className="text-sm text-gray-500">
                            Choisissez votre option
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-col gap-3">
                        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 transition-all hover:border-primary">
                          <input
                            type="radio"
                            checked={!person.includeDobok}
                            onChange={() => {
                              updatePerson(person.id, {
                                includeDobok: false,
                                selectedSize: "",
                              });
                            }}
                            className="h-5 w-5 text-primary"
                            name={`dobok-choice-${person.id}`}
                          />
                          <span className="text-md text-gray-600">
                            Je possède déjà un dobok ou je souhaite
                            l&apos;acheter par mes propres moyens
                          </span>
                        </label>

                        <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 transition-all hover:border-primary">
                          <input
                            type="radio"
                            checked={person.includeDobok}
                            onChange={() => {
                              updatePerson(person.id, {
                                includeDobok: true,
                              });
                            }}
                            className="h-5 w-5 text-primary"
                            name={`dobok-choice-${person.id}`}
                          />
                          <span className="text-md text-gray-600">
                            Je souhaite acheter un dobok avec mon inscription
                          </span>
                        </label>

                        {person.includeDobok && (
                          <div className="mt-2 grid grid-cols-1 gap-3 pl-0 ph:pl-8 md:grid-cols-2 lg:grid-cols-3">
                            {additionalFeesData
                              .find((fee) => fee.type === "DOBOK")
                              ?.sizes?.map((size) => (
                                <label
                                  key={size.size}
                                  className="group relative flex cursor-pointer items-center"
                                >
                                  <input
                                    type="radio"
                                    className="peer hidden"
                                    checked={person.selectedSize === size.size}
                                    onChange={() => {
                                      updatePerson(person.id, {
                                        selectedSize: size.size,
                                      });
                                    }}
                                    name={`dobok-size-${person.id}`}
                                  />
                                  <div className="flex h-14 w-full items-center justify-between rounded-lg border bg-white px-4 shadow-sm transition-all duration-200 group-hover:shadow-md peer-checked:border-secondary peer-checked:bg-secondary peer-checked:shadow-md">
                                    <span className="ph:text-md text-xs font-light text-primary xs:text-sm">
                                      {size.size} ({size.heights.join(", ")})
                                    </span>
                                    <span className="font-medium text-primary">
                                      {size.price}€
                                    </span>
                                  </div>
                                </label>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {/* Person Total */}
            {person.selectedCategory && (
              <div className="flex h-16 items-center justify-between rounded-lg bg-gradient-to-r from-yellow-400 via-red-500 to-blue-600 px-6">
                <span className="text-md font-light uppercase tracking-wider text-white ph:text-lg">
                  Total {index === 0 ? "" : "(-10%)"}
                </span>
                <span className="text-2xl font-bold text-white ph:text-3xl">
                  {formatPrice(calculatePersonTotal(person, index))}€
                </span>
              </div>
            )}
          </div>
        ))}

        {/* Grand Total */}
        {people.length > 1 && people.some((p) => p.selectedCategory) && (
          <div className="flex h-20 items-center justify-between rounded-lg bg-primary px-8">
            <span className="text-lg font-medium uppercase tracking-wider text-white">
              Total Global
            </span>
            <span className="text-2xl font-bold text-white ph:text-3xl">
              {formatPrice(calculateGrandTotal())}€
            </span>
          </div>
        )}

        {/* Information Message */}

        <p className="text-sm text-gray-600">
          <span className="font-medium">Note :</span> Ce calculateur est fourni
          à titre informatif uniquement. Le formulaire d&apos;inscription ne
          comprend pas encore ces informations. Nous vous recontacterons pour
          finaliser vos choix, ou vous pourrez les finaliser directement au
          club.
        </p>

        {/* Inscription Button */}
        <div className="mt-4">
          <PrimaryButton
            text="INSCRIPTION"
            navigateTo="/inscription"
            variant="primary"
            disabled={!isFormComplete()}
          />
        </div>
      </div>
    </section>
  );
};
